<template>
  <div>
    <h3>Shift Payments</h3>
    <a-table
      class="shifts-to-pay-table"
      size="small"
      :row-selection="rowSelection"
      :columns="columns"
      :pagination="false"
      :data-source="paymentInfo.shift_payments"
      :row-key="record => record.shift_payment.id"
      :scroll="{ y: 380, x: 900 }"
      :customRow="
        record => ({
          on: {
            click: () => handleOpenShift(record.shift.id)
          }
        })
      "
    >
      <span slot="date" slot-scope="text, record">
        {{ formatDateRange(record.shift.work_start, record.shift.work_end) }}
      </span>
      <span slot="status" slot-scope="text, record">
        <a-tag
          v-if="record.shift_payment.review_info && record.shift_payment.review_info.status"
          class="m-0"
          :color="getShiftPaymentStatus(record.shift_payment.review_info.status).color"
          >{{ getShiftPaymentStatus(record.shift_payment.review_info.status).title }}</a-tag
        >
        <span v-else>-</span>
      </span>
      <span slot="reviewer" slot-scope="text, record">
        <div v-if="record.shift_payment.review_info">
          <grouped-associate-info
            :first-name="record.shift_payment.review_info.reviewer.first_name"
            :last-name="record.shift_payment.review_info.reviewer.last_name"
            :photo-url="record.shift_payment.review_info.reviewer.photo_url"
            :associate-number="record.shift_payment.review_info.reviewer.associate_number"
          />
          <div class="review-date">
            {{ formatDate(record.shift_payment.review_info.reviewed_at) }}
          </div>
        </div>

        <span v-else>-</span>
      </span>
      <span slot="associate" slot-scope="text, record">
        <grouped-associate-info
          :first-name="record.associate.first_name"
          :last-name="record.associate.last_name"
          :photo-url="record.associate.photo_url"
          :associate-number="record.associate.associate_number"
        />
      </span>
      <template slot="comment" slot-scope="text, record">
        <editable-cell
          v-if="!readOnly"
          :text="record.shift_payment.comment"
          @change="onCellChange(record.shift_payment, 'name', $event)"
        />
        <span v-else>{{ record.shift_payment.comment || '-' }}</span>
      </template>
    </a-table>
  </div>
</template>

<script>
import { Table, Tag } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import GroupedAssociateInfo from '@/components/ui/grouped-associate-info.vue'
import TableEditableCell from '@/components/entities/table/table-editable-cell.vue'
import util from '@/util'
import moment from 'moment-timezone'

const { mapGetters: shiftGetters } = createNamespacedHelpers('shifts')
const { mapActions: paymentActions } = createNamespacedHelpers('payment')
const { mapActions: modalActions } = createNamespacedHelpers('modals')

export default {
  components: {
    'a-table': Table,
    'a-tag': Tag,
    'editable-cell': TableEditableCell,
    'grouped-associate-info': GroupedAssociateInfo
  },
  props: {
    paymentInfo: {
      type: Object,
      required: true
    },
    selectedRowKeys: {
      type: Array,
      default: () => []
    },
    availableValidation: {
      type: Boolean,
      default: false
    },
    readOnly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      instant_loan_percent: this.$store.state.applicationState.currentProject.payment_settings.instant_loan_percent
    }
  },
  computed: {
    ...shiftGetters(['getShiftPaymentStatus']),

    isStaff() {
      return this.$store.state.applicationState.currentProject.staffing
    },

    columns() {
      if (this.paymentInfo.shift_payments.some(item => item.shift_payment.review_info)) {
        return [
          {
            title: 'Date',
            key: 'date',
            width: 100,
            scopedSlots: { customRender: 'date' }
          },
          {
            title: 'Status',
            key: 'status',
            width: 120,
            scopedSlots: { customRender: 'status' }
          },
          {
            title: 'Reviewer',
            key: 'reviewer',
            width: 200,
            scopedSlots: { customRender: 'reviewer' }
          },
          {
            title: 'Associate',
            key: 'associate',
            width: 200,
            scopedSlots: { customRender: 'associate' }
          },
          {
            title: 'Comment',
            key: 'comment',
            width: 200,
            scopedSlots: { customRender: 'comment' }
          }
        ]
      }

      return [
        {
          title: 'Date',
          key: 'date',
          scopedSlots: { customRender: 'date' }
        },
        {
          title: 'Associate',
          key: 'associate',
          scopedSlots: { customRender: 'associate' }
        }
      ]
    },

    rowSelection() {
      return !this.availableValidation
        ? null
        : {
            selectedRowKeys: this.selectedRowKeys,
            onChange: this.onSelectChange,
            getCheckboxProps: record => ({
              props: {
                disabled: !!record.shift_payment.review_info
              }
            })
          }
    }
  },
  methods: {
    ...paymentActions(['updateShiftPayments']),
    ...modalActions(['openModal']),

    onSelectChange(selectedRowKeys) {
      this.$emit('update:selectedRowKeys', selectedRowKeys)
    },

    handleOpenShift(id) {
      this.openModal({
        type: 'shiftView',
        data: {
          visible: true,
          edit: () => {},
          shift_id: id,
          open_tab: 'info'
        }
      })
    },

    formatDate(date) {
      return util.convertDateTimeToCurrentTimeZoneWithFormat(date, this.$store.state.applicationState.dateFormat)
    },

    formatDateRange(start_date, end_date) {
      if (!end_date || moment(end_date).isSame(start_date, 'day')) {
        return this.formatDate(start_date)
      }

      return `${this.formatDate(start_date)} - ${this.formatDate(end_date)}`
    },

    async onCellChange(shift_payment, dataIndex, value) {
      if (this.readOnly) return

      if (typeof value === 'string' && value.trim() === shift_payment[dataIndex].trim()) {
        return
      }

      try {
        const payload = {
          shift_payment_ids: [shift_payment.id],
          request_id: this.paymentInfo.payment.id,
          comment: typeof value === 'string' ? value.trim() : value
        }

        await this.updateShiftPayments(payload)
        this.$emit('comment-updated', {
          shiftPaymentIds: payload.shift_payment_ids,
          comment: payload.comment
        })
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error.message)
      }
    }
  }
}
</script>

<style scoped>
.review-date {
  font-weight: 600;
}
</style>
